// @flow
/** @jsx jsx */
import React, { useContext, useEffect, useMemo } from 'react';
import universal from '@core-ui/universal';
import { BreakpointContext, XLARGE } from '@core-ui/breakpoint-provider';
import { jsx } from '@core-ui/react-stitch';
import { useSitewide } from '@mfe/sitewide';
import { useMediator } from '@core-ui/mediator';
import { useLocalize } from '@core-ui/localization-provider';
// $FlowFixMe
import useSortBy from '../../hooks/useSortBy';
// $FlowFixMe
import useSortByRatings from '../../hooks/features/useSortByRatings';
// $FlowFixMe
import usePmcsMarketing from '../../hooks/features/usePmcsMarketing';
import { getDesktopExperience } from './experiences/desktop';
import { getDashboardExperience } from './experiences/dashboard';
import { getLegacyExperience } from './experiences/legacy';
import { adaptOrderedOptions } from '../product-grid-region/adapt-applied-options';

const Rail = universal(import('@cat/rail'));

type RailContainerProps = {
  abbrBrand: AbbrBrand,
  abbrNameForTealium: string,
  businessUnitId: number,
  categoryName: string,
  contentData: any,
  cid: string,
  datalayer: DataLayer,
  leftNav: LeftNavType,
  mediator: { [string]: any },
  pageType: string,
  selectedNodes: SelectedNodes,
  webHierarchy: any,
  isModelToggleEnabled: boolean,
  isMixedGridEnabled: boolean,
  isDashboardEnabled: boolean,
  isSortByRatingsEnabled: Boolean,
  isBRRedesignEnabled: Boolean,
  isBRRedesignOpacityEnabled: Boolean,
  isSwitchContractEnabled?: boolean,
  isOutOfStockFilterEnabled: boolean,
};

const getStickyBuffer = (isDesktop, itemCountAndClearButtonRef) => {
  return isDesktop ? itemCountAndClearButtonRef?.current?.offsetHeight ?? 0 : 0;
};

const shouldRenderLeftFacetedGrid = (isBRRedesignEnabled, isDesktop) => {
  return !isBRRedesignEnabled && isDesktop;
};

const doesPageIncludeTopBanner = (marketingData = {}, pmcsEnabled) => {
  const categoryContentDataTopContent = marketingData?.data?.components?.top;
  return (
    (!!categoryContentDataTopContent &&
      Object.keys(categoryContentDataTopContent).length) ||
    pmcsEnabled
  );
};

const RailContainer = (props: RailContainerProps) => {
  const {
    abbrBrand,
    abbrNameForTealium,
    businessUnitId,
    categoryName,
    cid,
    contentData,
    datalayer,
    leftNav,
    mediator,
    pageType,
    selectedNodes,
    webHierarchy,
    isModelToggleEnabled = false,
    isMixedGridEnabled = false,
    isDashboardEnabled = false,
    isBRRedesignEnabled = false,
    isBRRedesignOpacityEnabled = false,
    isSwitchContractEnabled,
    isOutOfStockFilterEnabled,
  } = props;

  const pmcsEnabled = usePmcsMarketing();
  const isSortByRatingsEnabled = useSortByRatings();
  const itemCountAndClearButtonRef = React.useRef();
  const { greaterOrEqualTo } = useContext(BreakpointContext);
  const { value: sortByValue, onChange: sortByOnChange } = useSortBy();
  const { state, publish } = useMediator(mediator);
  const { localize } = useLocalize();
  const {
    totalItemCount = 0,
    appliedFacetsCount = 0,
    ignoredFacets,
    orderedFacets,
    facets: { hasTags = false, facetOptions = [], appliedFacets = {} } = {},
  } = state;

  const categoryContentData = contentData?.category?.[cid];
  const hasMarketingDataEBB = doesPageIncludeTopBanner(
    categoryContentData,
    pmcsEnabled
  );
  const isDesktop = greaterOrEqualTo(XLARGE);
  const hasLeftFacetedGrid = shouldRenderLeftFacetedGrid(
    isBRRedesignEnabled,
    isDesktop
  );
  const stickyBuffer = getStickyBuffer(isDesktop, itemCountAndClearButtonRef);
  const { totalStickyElementHeight, setIsMegaNavSticky } = useSitewide();

  const megaNavUIData = {
    height: totalStickyElementHeight,
    toggleSticky: setIsMegaNavSticky,
  };

  useEffect(() => {
    publish('updateMegaNavHeightScroll', [megaNavUIData]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalStickyElementHeight]);

  const appliedOptions = useMemo(
    () => adaptOrderedOptions(orderedFacets, ignoredFacets),
    [orderedFacets, ignoredFacets]
  );

  const railContent = hasLeftFacetedGrid
    ? getDesktopExperience({
        localize,
        abbrBrand,
        selectedNodes,
        totalItemCount,
        hasTags,
        mediator,
        publish,
        abbrNameForTealium,
        businessUnitId,
        categoryName,
        datalayer,
        pageType,
        facetOptions,
        appliedFacets,
        leftNav,
        itemCountAndClearButtonRef,
        isSwitchContractEnabled,
        isOutOfStockFilterEnabled,
      })
    : isDashboardEnabled
    ? getDashboardExperience({
        localize,
        totalItemCount,
        hasTags,
        mediator,
        publish,
        facetOptions,
        appliedFacets,
        appliedOptions,
        abbrNameForTealium,
        businessUnitId,
        categoryName,
        datalayer,
        pageType,
        selectedNodes,
        webHierarchy,
        abbrBrand,
        isModelToggleEnabled,
        isMixedGridEnabled,
        sortByValue,
        sortByOnChange,
        isSortByRatingsEnabled,
        isSwitchContractEnabled,
        isOutOfStockFilterEnabled,
      })
    : getLegacyExperience({
        localize,
        totalItemCount,
        mediator,
        publish,
        facetOptions,
        appliedFacets,
        appliedOptions,
        abbrNameForTealium,
        businessUnitId,
        categoryName,
        datalayer,
        pageType,
        selectedNodes,
        webHierarchy,
        hasTags,
        sortByValue,
        sortByOnChange,
        isSortByRatingsEnabled,
        abbrBrand,
        isBRRedesignEnabled,
        isBRRedesignOpacityEnabled,
        isSwitchContractEnabled,
        isOutOfStockFilterEnabled,
      });

  const onFacetChange = (option) => {
    publish('updateFacets', [option]);
  };

  return (
    <>
      <Rail
        appliedFacetsCount={appliedFacetsCount}
        hasLeftFacetedGrid={hasLeftFacetedGrid}
        hasMarketingDataEBB={hasMarketingDataEBB}
        isBRRedesignEnabled={isBRRedesignEnabled}
        isBRRedesignOpacityEnabled={isBRRedesignOpacityEnabled}
        isModelToggleEnabled={isModelToggleEnabled}
        newDashboard={isDashboardEnabled}
        onFacetChange={onFacetChange}
        stickyBuffer={stickyBuffer}
      >
        {railContent}
      </Rail>
      <div className="cat-drawer" />
    </>
  );
};

export default RailContainer;
